import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../componentes/Shell.jsx';
import { guardarConConexion } from '../nucleo/api/cliente.js';
import {
  actualizarUsuarioDelHogar,
  cambiarEstadoDeUsuario,
  consultarMonedas,
  consultarUsuariosDelHogar,
  crearUsuarioDelHogar,
  restablecerContrasenaDeOtroUsuario,
} from '../nucleo/api/nucleoApi.js';
import { formatearInstante } from '../nucleo/fecha/fechaLocal.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';
import ConfirmacionGeneral from '../nucleo/componentes/ConfirmacionGeneral.jsx';
import '../estilos/catalogos-con-confirmacion.css';

/**
 * Personas del hogar (RF-025, RN-040, RN-043, D-09).
 *
 * El nombre del archivo, de la función y de la ruta `/ajustes/usuarios` NO cambian
 * con el renombrado del incremento 16 (RN-647, D-347): no son texto visible, y
 * cambiarlos rompería marcadores guardados sin ganancia para quien la usa. Lo que
 * cambia es el TÍTULO de la pantalla y los rótulos que la nombran en otras
 * pantallas.
 *
 * La lista de lo administrable sobre la otra persona es CERRADA y son cuatro
 * cosas: dar de alta, editar su nombre, restablecer su contraseña y
 * desactivar/reactivar. Su moneda predeterminada se muestra en SOLO LECTURA: la
 * cambia esa persona desde su perfil (RN-038).
 *
 * D-09: desactivar advierte ANTES de confirmar que se cerrará su sesión en todos
 * sus dispositivos y que lo no guardado se pierde (RN-043f, RN-046d). Reactivar
 * NO pide confirmación (RN-042, RN-043e).
 *
 * ---------------------------------------------------------------------------
 * QUÉ CAMBIÓ EN EL INCREMENTO 16 (RF-226, RF-227, RN-636 a RN-639) Y QUÉ NO
 *
 * La confirmación la presenta el componente único del núcleo —con posición fija,
 * cortina y foco dentro— en lugar de la tarjeta en línea de antes, que era el
 * mismo patrón del defecto H-09. El enlace a «sesión finalizada» viaja por la
 * RANURA DE CONTENIDO que el componente ya tiene desde el incremento 12 (D-308):
 * usarla es consumir el contrato, no tocar el núcleo (RN-637).
 *
 * DEFECTO CORREGIDO (RN-638): la pregunta se cerraba ANTES de ejecutar —calco del
 * defecto que RN-505 documentó en Categorías—, así que un fallo dejaba a la persona
 * sin la pregunta enfrente y con el aviso lejos. Ahora pasa a «en curso» y se
 * cierra SOLO al conocer el resultado.
 *
 * LO QUE NO SE TOCÓ, Y ES A PROPÓSITO: la operación, sus dos salvaguardas del
 * servidor y sus acuses. La autodesactivación sigue sin ofrecerse (el botón no se
 * dibuja para quien está en sesión) y «último usuario activo» sigue siendo
 * salvaguarda del servidor, inalcanzable desde aquí (RN-641).
 * ---------------------------------------------------------------------------
 */

/*
 * TEXTOS NORMATIVOS DE RN-636. Son transcripción de los de hoy: el único ajuste
 * declarado es de REPARTO —lo que era un bloque de ayuda pasa a dos renglones de
 * detalle, porque el contrato pinta el detalle como lista—, sin cambiar palabras.
 */
export const TITULO_DE_LA_PANTALLA = 'Personas del hogar';

export const TITULO_DE_LA_BAJA = (nombre) => `¿Desactivar el acceso de ${nombre}?`;

export const CONSECUENCIA_DE_LA_BAJA = 'Se cerrará su sesión en todos sus dispositivos, de inmediato y sin esperar a que caduque. Si estaba capturando algo, lo no guardado se pierde.';

export const DETALLE_DE_LA_BAJA = [
  { clave: 'datos', texto: 'Sus datos siguen visibles y conserva su moneda predeterminada.' },
  { clave: 'reactivacion', texto: 'Al reactivarla tendrá que iniciar sesión de nuevo.' },
];

export const ENLACE_DE_LA_BAJA = 'Ver lo que aparece en el dispositivo de esa persona';

export const ROTULO_DE_ACEPTAR_LA_BAJA = 'Sí, desactivar y cerrar sus sesiones';
export const ROTULO_MIENTRAS_DESACTIVA = 'Desactivando…';

export const ACUSE_CON_SESIONES = 'Acceso desactivado y sesiones cerradas.';
export const ACUSE_SIN_SESIONES = 'Acceso desactivado.';

export default function UsuariosDelHogar() {
  const [usuarios, setUsuarios] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [porDesactivar, setPorDesactivar] = useState(null);
  const [desactivando, setDesactivando] = useState(false);
  const [alta, setAlta] = useState({ abierta: false, nombre: '', clave: '', contrasenaInicial: '', idMonedaPredeterminada: '' });

  /*
   * EL ANCLA de las acciones de acceso: el acuse y el error se pintan EN EL SITIO
   * DEL RENGLÓN de esa persona (RN-639, D-348). Las otras tres acciones —alta,
   * nombre, contraseña— conservan su acuse de arriba: no son acciones de renglón y
   * este incremento no las toca.
   */
  const [ancla, setAncla] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [lista, catalogoDeMonedas] = await Promise.all([
        consultarUsuariosDelHogar(false),
        consultarMonedas(true),
      ]);
      // Una respuesta que no llega no se disfraza de lista vacía con contenido: se
      // normaliza para que el recorrido del renglón no se rompa.
      const catalogo = catalogoDeMonedas ?? [];
      setUsuarios(lista ?? []);
      setMonedas(catalogo);
      setAlta((anterior) => ({
        ...anterior,
        idMonedaPredeterminada: anterior.idMonedaPredeterminada
          || catalogo.find((m) => m.codigoISO === 'MXN')?.idMoneda
          || catalogo[0]?.idMoneda
          || '',
      }));
    } catch (fallo) {
      setError(fallo);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const nombreDeMoneda = (idMoneda) => monedas.find((m) => m.idMoneda === idMoneda)?.codigoISO ?? '—';

  const limpiarAvisos = () => { setError(null); setAviso(null); setAncla(null); };

  async function darDeAlta() {
    limpiarAvisos();
    try {
      await guardarConConexion(() => crearUsuarioDelHogar({
        nombre: alta.nombre.trim(),
        clave: alta.clave.trim(),
        contrasenaInicial: alta.contrasenaInicial,
        idMonedaPredeterminada: Number(alta.idMonedaPredeterminada),
      }));
      setAlta({ ...alta, abierta: false, nombre: '', clave: '', contrasenaInicial: '' });
      setAviso('Persona usuaria dada de alta. Deberá cambiar su contraseña al entrar.');
      await cargar();
    } catch (fallo) {
      setError(fallo);
    }
  }

  async function renombrar(usuario) {
    const nombre = window.prompt('Nombre para mostrar', usuario.nombre);
    if (!nombre) return;

    limpiarAvisos();
    try {
      await guardarConConexion(() => actualizarUsuarioDelHogar(usuario.idUsuario, { nombre, version: usuario.version }));
      setAviso('Nombre actualizado.');
      await cargar();
    } catch (fallo) {
      setError(fallo);
    }
  }

  async function restablecer(usuario) {
    const contrasenaNueva = window.prompt(
      `Contraseña nueva para ${usuario.nombre} (mínimo 8 caracteres).\nSe cerrarán sus sesiones abiertas y deberá cambiarla al entrar.`);
    if (!contrasenaNueva) return;

    limpiarAvisos();
    try {
      await guardarConConexion(() => restablecerContrasenaDeOtroUsuario(usuario.idUsuario, contrasenaNueva));
      setAviso(`Contraseña restablecida y sesiones cerradas. ${usuario.nombre} deberá cambiarla al entrar.`);
      await cargar();
    } catch (fallo) {
      setError(fallo);
    }
  }

  /** Reactivar: inmediato, sin diálogo (RN-042, RN-043e). */
  async function reactivar(usuario, indice) {
    limpiarAvisos();
    try {
      await guardarConConexion(() => cambiarEstadoDeUsuario(usuario.idUsuario, true, usuario.version));
      setAncla({
        idUsuario: usuario.idUsuario,
        indice,
        exito: `${usuario.nombre} vuelve a tener acceso. Tendrá que iniciar sesión de nuevo.`,
      });
      await cargar();
    } catch (fallo) {
      setAncla({ idUsuario: usuario.idUsuario, indice, error: fallo });
    }
  }

  /*
   * La pantalla ejecuta la operación; el componente NO la ejecuta y NO se cierra
   * solo (§7 bis). La pregunta pasa a «en curso» y se cierra al conocer el
   * resultado: ni antes (defecto de hoy, RN-638) ni con un quinto estado de error
   * dentro del componente, que §7 bis prohíbe.
   */
  async function confirmarDesactivacion() {
    if (desactivando) return;
    const { usuario, indice } = porDesactivar;
    setDesactivando(true);
    try {
      const resultado = await guardarConConexion(() => cambiarEstadoDeUsuario(usuario.idUsuario, false, usuario.version));
      setPorDesactivar(null);
      setAncla({
        idUsuario: usuario.idUsuario,
        indice,
        exito: resultado.sesionesRevocadas ? ACUSE_CON_SESIONES : ACUSE_SIN_SESIONES,
      });
      await cargar();
    } catch (fallo) {
      // RN-639 / D-348 · FORMA ÚNICA DE FALLO: la pregunta se cierra y el error se
      // ancla donde está el renglón. La persona SIGUE con acceso.
      setPorDesactivar(null);
      setAncla({ idUsuario: usuario.idUsuario, indice, error: fallo });
    } finally {
      setDesactivando(false);
    }
  }

  /*
   * D-349 · Tocar el enlace de la ranura ABANDONA la confirmación sin enviar nada
   * —equivale a cancelar— y la baja pendiente se descarta. Al volver, la pantalla
   * está en reposo: ni pregunta reabierta ni persona pendiente. Recordar ese estado
   * sería comportamiento nuevo y queda fuera de este incremento.
   */
  const abandonarPorElEnlace = () => setPorDesactivar(null);

  let posicionDelAncla = -1;
  if (ancla) {
    const encontrado = usuarios.findIndex((u) => u.idUsuario === ancla.idUsuario);
    posicionDelAncla = encontrado >= 0 ? encontrado : Math.min(ancla.indice, usuarios.length);
  }

  const anclaPintada = ancla && (
    ancla.error
      ? (
        <AvisoDeError
          key="ancla"
          error={ancla.error}
          margen="0 0 10px"
          alCerrar={() => setAncla(null)}
          datoDePrueba="aviso-de-error-de-la-accion"
        />
      )
      : (
        <div
          key="ancla"
          className="aviso n ancla-de-la-accion"
          role="status"
          data-prueba="acuse-de-la-accion"
          data-estado="exito"
        >
          <button
            type="button"
            className="ancla-de-la-accion__cerrar"
            onClick={() => setAncla(null)}
            aria-label="Cerrar el aviso"
          >
            ✕
          </button>
          ✔ {ancla.exito}
        </div>
      )
  );

  return (
    <Shell titulo={TITULO_DE_LA_PANTALLA} volverA="/ajustes">
      <AvisoDeError error={error} />
      {aviso && <div className="aviso n">✔ {aviso}</div>}

      <div className="aviso i">
        No hay rol de administrador: cualquiera de las dos personas puede dar de alta a la otra,
        editar su nombre, restablecerle la contraseña y activar o desactivar su acceso. Esa lista es
        cerrada.
      </div>

      {/*
        * UN RENGLÓN POR PERSONA, DE DOS LÍNEAS: identidad arriba, acciones abajo
        * (forma del prototipo aprobado `diseno/prototipo-v35-personas-del-hogar.html`).
        * Antes eran dos listas separadas —primero todas las identidades y después
        * todas las filas de acciones—, y con eso «el sitio del renglón» de esa
        * persona no era un lugar: el aviso anclado de RN-639 no tendría dónde ir.
        */}
      <div className="lista-con-anclas" data-prueba="lista-de-personas">
        {usuarios.flatMap((usuario, indice) => {
          const renglon = (
            <div
              key={usuario.idUsuario}
              className={`renglon-con-acciones${usuario.activo ? '' : ' renglon-con-acciones--inactivo'}`}
              data-prueba="renglon-de-persona"
              data-estado={usuario.activo ? 'con-acceso' : 'sin-acceso'}
              data-persona={usuario.nombre}
            >
              {/* LÍNEA 1 · IDENTIDAD. Ningún control vive aquí. */}
              <div className="renglon-con-acciones__identidad">
                <div
                  className="punto"
                  style={{ background: usuario.activo ? 'var(--lnk)' : 'var(--sec)' }}
                  aria-hidden="true"
                >
                  {usuario.nombre.slice(0, 2).toUpperCase()}
                </div>

                <div className="crece renglon-con-acciones__texto">
                  <b data-prueba="nombre-de-la-persona">
                    {usuario.nombre}
                    {usuario.esUsuarioEnSesion && <span className="chip" style={{ marginLeft: 6 }}>Tú</span>}
                  </b>
                  <small>
                    Clave: {usuario.clave} · Moneda predeterminada: {nombreDeMoneda(usuario.idMonedaPredeterminada)}
                    {!usuario.esUsuarioEnSesion && ' (la cambia esa persona desde su perfil)'}
                  </small>
                  {!usuario.activo && (
                    <small>
                      <span className="chip e" data-prueba="estado-de-la-persona">✖ Desactivada</span>{' '}
                      {usuario.fechaBaja && `Acceso cortado el ${formatearInstante(usuario.fechaBaja)}`}
                    </small>
                  )}
                </div>
              </div>

              {/* LÍNEA 2 · LAS ACCIONES, las mismas de hoy y en su orden. */}
              <div className="renglon-con-acciones__acciones">
                <button
                  type="button"
                  className="btn sec"
                  onClick={() => renombrar(usuario)}
                  data-prueba="accion-editar-nombre"
                >
                  Editar nombre
                </button>
                {!usuario.esUsuarioEnSesion && (
                  <>
                    <button
                      type="button"
                      className="btn sec"
                      onClick={() => restablecer(usuario)}
                      data-prueba="accion-restablecer-contrasena"
                    >
                      Restablecer contraseña
                    </button>
                    {usuario.activo
                      ? (
                        <button
                          type="button"
                          className="btn sec"
                          onClick={() => setPorDesactivar({ usuario, indice })}
                          data-prueba="accion-desactivar-acceso"
                        >
                          Desactivar acceso
                        </button>
                      )
                      : (
                        <button
                          type="button"
                          className="btn sec"
                          onClick={() => reactivar(usuario, indice)}
                          data-prueba="accion-reactivar-acceso"
                        >
                          Reactivar acceso
                        </button>
                      )}
                  </>
                )}
              </div>
            </div>
          );

          return indice === posicionDelAncla ? [anclaPintada, renglon] : [renglon];
        })}

        {ancla && posicionDelAncla >= usuarios.length && anclaPintada}
      </div>

      {/*
        * RF-226 · La pregunta, con el componente único del núcleo. El enlace va en
        * la ranura de contenido (`children`), que el componente ya tiene: la
        * pantalla responde por su contenido y el componente no lo lee ni lo valida
        * (RN-637). Este flujo no pasa advertencia.
        */}
      <ConfirmacionGeneral
        abierta={porDesactivar !== null}
        pregunta={porDesactivar ? TITULO_DE_LA_BAJA(porDesactivar.usuario.nombre) : ''}
        consecuencia={CONSECUENCIA_DE_LA_BAJA}
        detalle={porDesactivar ? DETALLE_DE_LA_BAJA : null}
        rotuloDeAceptar={ROTULO_DE_ACEPTAR_LA_BAJA}
        rotuloEnCurso={ROTULO_MIENTRAS_DESACTIVA}
        enCurso={desactivando}
        alAceptar={confirmarDesactivacion}
        alCancelar={() => setPorDesactivar(null)}
        datoDePrueba="confirmacion-de-baja-de-persona"
      >
        <Link
          to="/sesion-finalizada"
          onClick={abandonarPorElEnlace}
          data-prueba="enlace-de-sesion-finalizada"
        >
          {ENLACE_DE_LA_BAJA}
        </Link>
      </ConfirmacionGeneral>

      {/* Alta de la otra persona: única vez en que se captura su moneda (RN-039). */}
      {alta.abierta ? (
        <div className="card">
          <h3>Dar de alta a la otra persona</h3>

          <label htmlFor="nombreAlta">Nombre para mostrar</label>
          <input id="nombreAlta" value={alta.nombre} onChange={(e) => setAlta({ ...alta, nombre: e.target.value })} />

          <label htmlFor="claveAlta">Clave de acceso</label>
          <input id="claveAlta" value={alta.clave} onChange={(e) => setAlta({ ...alta, clave: e.target.value })} />

          <label htmlFor="contrasenaAlta">Contraseña inicial</label>
          <input id="contrasenaAlta" type="password" placeholder="Mínimo 8 caracteres" value={alta.contrasenaInicial} onChange={(e) => setAlta({ ...alta, contrasenaInicial: e.target.value })} />
          <div className="ayuda">Deberá cambiarla en su primer inicio de sesión.</div>

          <label htmlFor="monedaAlta">Moneda predeterminada (valor inicial)</label>
          <select id="monedaAlta" value={alta.idMonedaPredeterminada} onChange={(e) => setAlta({ ...alta, idMonedaPredeterminada: e.target.value })}>
            {monedas.map((moneda) => (
              <option key={moneda.idMoneda} value={moneda.idMoneda}>{moneda.codigoISO} · {moneda.nombre}</option>
            ))}
          </select>
          <div className="ayuda">
            Es un valor inicial, no una edición: en cuanto esa persona entre, solo ella podrá
            cambiarlo. <b>No se le pregunta zona horaria</b> ni tema.
          </div>

          <button className="btn ancho" onClick={darDeAlta}>Dar de alta</button>
          <button className="btn sec ancho" onClick={() => setAlta({ ...alta, abierta: false })}>Cancelar</button>
        </div>
      ) : (
        <div style={{ margin: '10px 12px' }}>
          <button className="btn ancho" onClick={() => setAlta({ ...alta, abierta: true })}>
            Dar de alta a otra persona
          </button>
        </div>
      )}

      {/* Esta prosa NO entra en el renombrado de RN-647: habla de las personas
          usuarias, no nombra la pantalla, y reescribirla sería exceder el encargo. */}
      <div className="info">
        Los usuarios no se eliminan: se desactivan, y sus datos siguen visibles para el resto. Debe quedar siempre al menos una persona activa y nadie puede desactivarse a sí
        misma.
      </div>
    </Shell>
  );
}
